import React, { FC, useState } from "react";

import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";

import { ArrowRight, LinkIcon, LocalIcon, MailIcon, PhoneIcon } from "@/assets/icons";
import { sanitizeHtml } from "@/core/security";
import { UrlManager } from "@/shared/apis/UrlManager";

import { StyledBrokerLogoLi, StyledBrokerWelcomeTextLi, StyledSeeMoreLi } from "./styled";

type CurrentBrokerInfoProps = {
    currentStateBroker: MyBrokerApi.Broker;
    handleClickAddress: () => void;
    handleClickPhone: () => void;
    handleClickEmail: () => void;
    handleClickUrl: () => void;
    hideSeeMore: boolean;
};

const CurrentBrokerInfo: FC<CurrentBrokerInfoProps> = ({
    currentStateBroker,
    handleClickAddress,
    handleClickPhone,
    handleClickEmail,
    handleClickUrl,
    hideSeeMore,
}) => {
    const { t } = useTranslation();
    const [lineClamp, setLineClamp] = useState(true);
    const welcomeText = sanitizeHtml(currentStateBroker?.welcomeText?.name, { ALLOWED_TAGS: [] });

    const handleSeeMoreWelcomeText = () => {
        if (lineClamp) {
            setLineClamp(false);
        } else {
            setLineClamp(true);
        }
    };

    return (
        <>
            <StyledBrokerLogoLi>
                {currentStateBroker?.officeId && (
                    <img alt="" src={UrlManager.brokerLogo(currentStateBroker?.officeId)} />
                )}
            </StyledBrokerLogoLi>
            {welcomeText && (
                <>
                    <StyledBrokerWelcomeTextLi>
                        <div id="welcomeText" className={clsx("welcome-text", lineClamp && "lineClamp")}>
                            <p>{welcomeText}</p>
                        </div>
                    </StyledBrokerWelcomeTextLi>
                    {!hideSeeMore && (
                        <StyledSeeMoreLi
                            data-testid="my_broker_selector-button-see_more"
                            onClick={handleSeeMoreWelcomeText}
                        >
                            {lineClamp ? t("Common.SeeAll") : t("Common.SeeLess")}
                            <ArrowRight />
                        </StyledSeeMoreLi>
                    )}
                </>
            )}
            {currentStateBroker?.address && (
                <MenuItem
                    style={{ padding: "10px 25px" }}
                    classes={{ root: "menuitem-root" }}
                    data-testid="my_broker_selector-option-address"
                    onClick={handleClickAddress}
                >
                    <ListItemIcon classes={{ root: "menuitem-icon-root" }}>
                        <LocalIcon />
                    </ListItemIcon>
                    <ListItemText
                        classes={{ root: "menuitem-text-root" }}
                        primary={`${currentStateBroker?.address} ${currentStateBroker?.zipCode} ${currentStateBroker?.city}`}
                    />
                </MenuItem>
            )}
            {currentStateBroker?.phone && (
                <MenuItem
                    style={{ padding: "10px 25px" }}
                    classes={{ root: "menuitem-root" }}
                    data-testid="my_broker_selector-option-phone"
                    onClick={handleClickPhone}
                >
                    <ListItemIcon classes={{ root: "menuitem-icon-root" }}>
                        <PhoneIcon />
                    </ListItemIcon>
                    <ListItemText classes={{ root: "menuitem-text-root" }} primary={currentStateBroker?.phone} />
                </MenuItem>
            )}
            {currentStateBroker?.email && (
                <MenuItem
                    style={{ padding: "10px 25px" }}
                    classes={{ root: "menuitem-root" }}
                    data-testid="my_broker_selector-option-mail"
                    onClick={handleClickEmail}
                >
                    <ListItemIcon classes={{ root: "menuitem-icon-root" }}>
                        <MailIcon />
                    </ListItemIcon>
                    <ListItemText classes={{ root: "menuitem-text-root" }} primary={currentStateBroker?.email} />
                </MenuItem>
            )}
            {currentStateBroker?.url && (
                <MenuItem
                    style={{ padding: "10px 25px" }}
                    classes={{ root: "menuitem-root" }}
                    data-testid="my_broker_selector-option-url"
                    onClick={handleClickUrl}
                >
                    <ListItemIcon classes={{ root: "menuitem-icon-root" }}>
                        <LinkIcon />
                    </ListItemIcon>
                    <ListItemText classes={{ root: "menuitem-text-root" }} primary={currentStateBroker?.url} />
                </MenuItem>
            )}
        </>
    );
};

export default CurrentBrokerInfo;
